/**
 * R289b · Feb 2026 — Legacy import operations.
 *
 * Two admin-only actions:
 *   • Post per-customer opening-balance JEs from legacy invoice
 *     outstanding balances.
 *   • Owner-only bulk delete of every LEG-tagged import (with typed
 *     "DELETE" confirmation).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BookOpen, Trash2, AlertTriangle, CheckCircle2 } from "lucide-react";

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

export default function LegacyOpsDialog({ open, onClose, ws, isOwner, onDone }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [entryDate, setEntryDate] = useState(new Date().toISOString().slice(0, 10));
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);
  // R289g · Feb 2026 — Opening AP snapshot (mirror of the AR flow).
  const [apPreview, setApPreview] = useState(null);
  const [apLoading, setApLoading] = useState(false);
  const [apPosting, setApPosting] = useState(false);
  const [apCutoff, setApCutoff] = useState(new Date().toISOString().slice(0, 10));

  const loadPreview = async () => {
    if (!ws) return;
    setLoading(true);
    try {
      const r = await api.get(`/gst/workspaces/${ws}/imports/opening-balance/preview`);
      setPreview(r.data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setLoading(false); }
  };

  const loadApPreview = async () => {
    if (!ws) return;
    setApLoading(true);
    try {
      const r = await api.get(
        `/gst/workspaces/${ws}/imports/opening-ap/preview`,
        { params: { cutoff_date: apCutoff } });
      setApPreview(r.data);
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setApLoading(false); }
  };

  useEffect(() => { if (open) { loadPreview(); loadApPreview(); setConfirmText(""); } /* eslint-disable-next-line */ }, [open, ws]);

  const postOB = async () => {
    setPosting(true);
    try {
      const r = await api.post(
        `/gst/workspaces/${ws}/imports/opening-balance/post`,
        null, { params: { entry_date: entryDate } });
      toast.success(`Posted ${r.data.entries_posted} JEs · ₹${inr(r.data.total_outstanding)}`);
      await loadPreview();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setPosting(false); }
  };

  const postAp = async () => {
    setApPosting(true);
    try {
      const r = await api.post(
        `/gst/workspaces/${ws}/imports/opening-ap/post`,
        null, { params: { entry_date: entryDate, cutoff_date: apCutoff } });
      toast.success(`Posted ${r.data.entries_posted} AP JEs · ₹${inr(r.data.total_outstanding)}`);
      await loadApPreview();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setApPosting(false); }
  };

  const doDelete = async () => {
    if (confirmText !== "DELETE") { toast.error("Type DELETE exactly"); return; }
    setDeleting(true);
    try {
      const r = await api.delete(
        `/gst/workspaces/${ws}/imports/legacy?confirm=DELETE`);
      toast.success(`Wiped ${r.data.invoices_deleted} invoices · ${r.data.opening_balance_je_deleted} JEs`);
      onDone?.();
      onClose?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setDeleting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto"
                        data-testid="legacy-ops-dialog">
        <DialogHeader>
          <DialogTitle>Legacy import operations</DialogTitle>
          <DialogDescription>
            Post opening balances or wipe legacy imports if a switchover test went wrong.
          </DialogDescription>
        </DialogHeader>

        {/* Opening balance section */}
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 space-y-3">
          <div className="flex items-center gap-2 font-semibold text-sm">
            <BookOpen className="w-4 h-4" /> Opening balance snapshot
          </div>
          <div className="text-[11px] text-muted-foreground leading-relaxed">
            Creates one balanced Journal Entry per customer with unpaid legacy invoices —
            <span className="font-mono"> Dr AR (1100) · Cr Opening Balance Equity (3200)</span>.
            Idempotent: re-running only posts newly-imported customers.
          </div>

          {loading ? (
            <div className="text-xs text-muted-foreground py-2">Loading preview…</div>
          ) : (preview?.rows?.length || 0) === 0 ? (
            <div className="rounded bg-muted/40 p-3 text-xs text-muted-foreground text-center">
              Nothing outstanding — all legacy customers already have opening JEs.
            </div>
          ) : (
            <>
              <div className="border border-border rounded overflow-hidden max-h-64 overflow-y-auto">
                <table className="w-full text-xs" data-testid="ob-preview-table">
                  <thead className="bg-muted/40 text-muted-foreground sticky top-0">
                    <tr>
                      <th className="text-left p-2">Customer</th>
                      <th className="text-right p-2">Invoices</th>
                      <th className="text-right p-2">Outstanding</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="p-2">{r.customer_name}</td>
                        <td className="p-2 text-right tabular-nums">{r.invoice_count}</td>
                        <td className="p-2 text-right tabular-nums font-semibold">
                          {r.currency === "INR" ? "₹" : r.currency + " "}{inr(r.outstanding)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-muted/30 font-semibold">
                    <tr>
                      <td className="p-2">Total across {preview.total_customers} customers</td>
                      <td></td>
                      <td className="p-2 text-right tabular-nums">₹{inr(preview.total_outstanding)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <Label className="text-xs">JE entry date</Label>
                  <Input type="date" value={entryDate}
                            onChange={(e) => setEntryDate(e.target.value)}
                            data-testid="ob-entry-date" />
                </div>
                <Button onClick={postOB} disabled={posting}
                          data-testid="ob-post-btn">
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  {posting ? "Posting…" : `Post ${preview.rows.length} JEs`}
                </Button>
              </div>
            </>
          )}
        </div>

        {/* R289g · Feb 2026 — Opening AP snapshot (mirror of AR flow). */}
        <div className="rounded-lg border border-border p-4 space-y-3">
          <div>
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <BookOpen className="w-4 h-4" /> Opening AP snapshot
            </h3>
            <p className="text-[11px] text-muted-foreground mt-1">
              Creates one balanced JE per vendor with an outstanding bill balance
              — <code className="text-[10px]">Dr Opening Balance Equity (3200) · Cr Accounts Payable (2100)</code>.
              Idempotent: re-runs only post newly-imported vendors.
            </p>
          </div>
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs">Cutoff date (bills on/before)</Label>
              <Input type="date" value={apCutoff}
                        onChange={(e) => { setApCutoff(e.target.value); setApPreview(null); }}
                        onBlur={loadApPreview}
                        data-testid="ap-cutoff-date" />
            </div>
          </div>
          {apLoading ? (
            <div className="text-xs text-muted-foreground py-2">Loading vendor preview…</div>
          ) : (apPreview?.rows?.length || 0) === 0 ? (
            <div className="text-xs text-muted-foreground py-3 border border-dashed border-border rounded text-center">
              No outstanding vendor bills on/before {apCutoff}.
            </div>
          ) : (
            <>
              <div className="border border-border rounded overflow-hidden max-h-64 overflow-y-auto">
                <table className="w-full text-xs" data-testid="ap-preview-table">
                  <thead className="bg-muted/40 text-muted-foreground sticky top-0">
                    <tr>
                      <th className="text-left p-2">Vendor</th>
                      <th className="text-right p-2">Bills</th>
                      <th className="text-right p-2">Outstanding</th>
                    </tr>
                  </thead>
                  <tbody>
                    {apPreview.rows.map((r, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="p-2">{r.vendor_name}</td>
                        <td className="p-2 text-right tabular-nums">{r.bill_count}</td>
                        <td className="p-2 text-right tabular-nums font-semibold">
                          {r.currency === "INR" ? "₹" : r.currency + " "}{inr(r.outstanding)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-muted/30 font-semibold">
                    <tr>
                      <td className="p-2">Total across {apPreview.total_vendors} vendors</td>
                      <td></td>
                      <td className="p-2 text-right tabular-nums">₹{inr(apPreview.total_outstanding)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="flex items-end justify-end">
                <Button onClick={postAp} disabled={apPosting}
                          data-testid="ap-post-btn">
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  {apPosting ? "Posting…" : `Post ${apPreview.rows.length} AP JEs`}
                </Button>
              </div>
            </>
          )}
        </div>

        {/* Danger zone — owner only */}
        {isOwner && (
          <div className="rounded-lg border border-rose-300 dark:border-rose-800
                             bg-rose-50 dark:bg-rose-950/40 p-4 space-y-3 mt-4"
                  data-testid="legacy-danger-zone">
            <div className="flex items-center gap-2 font-semibold text-sm text-rose-800 dark:text-rose-200">
              <AlertTriangle className="w-4 h-4" /> Bulk delete legacy imports
            </div>
            <div className="text-[11px] text-rose-700 dark:text-rose-300 leading-relaxed">
              Removes every <b>Legacy</b>-tagged invoice, resets the <b>LEG-YYYY-YY-NNNN</b>
              counter, and un-posts the linked opening-balance JEs.
              This <b>cannot be undone</b>. Type <span className="font-mono bg-rose-100 dark:bg-rose-900/60 px-1 rounded">DELETE</span> to confirm.
            </div>
            <div className="flex items-end gap-2">
              <Input placeholder="Type DELETE"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        className="font-mono"
                        data-testid="legacy-delete-confirm" />
              <Button variant="destructive" onClick={doDelete}
                         disabled={confirmText !== "DELETE" || deleting}
                         data-testid="legacy-delete-btn">
                <Trash2 className="w-4 h-4 mr-1" />
                {deleting ? "Wiping…" : "Wipe legacy"}
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onClose?.()}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
